var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { attr, FASTElement, observable, Updates } from "@microsoft/fast-element";
import { keyEnter, keySpace, keyEscape, uniqueId } from "@microsoft/fast-web-utilities";
import { toggleState } from "../../util/element-internals.js";
import { MenuList } from "../menu-list/menu-list.js";
/**
 * AccordionMenuPanel
 * @summary An AccordionMenuPanel component that provides expandable and collapsible sections within an accordion menu.
 *
 * @example
 * ```html
 * <fabric-accordion-menu>
 *   <fabric-accordion-menu-panel id="examplePanel" expanded>
 *     <div slot="heading">Section 1</div>
 *     <span slot="marker-expanded">-</span>
 *     <span slot="marker-collapsed">+</span>
 *     <fabric-menu-list>
 *       <fabric-menu-item>Content for section 1</fabric-menu-item>
 *       <fabric-menu-item>Content for section 2</fabric-menu-item>
 *     </fabric-menu-list>
 *   </fabric-accordion-menu-panel>
 * </fabric-accordion-menu>
 * ```
 *
 * @attr {boolean} expanded - Expands or collapses the item.
 * @attr {boolean} disabled - Disables the accordion item.
 * @attr {string} id - The item ID.
 * @attr {AccordionMenuPanelSize} size - Defines the accordion header font size.
 * @attr {boolean} block - Sets the width of the focus state.
 * @attr {AccordionMenuPanelMarkerPosition} marker-position - Sets expand and collapsed icon position.
 *
 * @prop {ElementInternals} elementInternals - Internal element state used to manage the component.
 * @prop {MenuList[] | undefined} menuList - The menu list element if present.
 * @prop {HTMLElement | undefined} headingElement - Reference to the heading element.
 * @prop {boolean} expanded - Expands or collapses the item.
 * @prop {boolean} disabled - Disables an accordion item
 * @prop {string} id - The item ID.
 * @prop {AccordionMenuPanelSize | undefined} size - Defines accordion header font size.
 * @prop {boolean} block - Sets the width of the focus state.
 * @prop {AccordionMenuPanelMarkerPosition | undefined} markerPosition - Sets expand and collapsed icon position.
 *
 * @slot heading - Slot for the heading content.
 * @slot marker-expanded - Slot for the expanded icon.
 * @slot marker-collapsed - Slot for the collapsed icon.
 * @slot - Default slot for the accordion item content.
 *
 * @csspart heading - Represents the heading of the accordion item.
 * @csspart heading-content - Represents the content container of the heading.
 * @csspart markers - Represents the container for expand/collapse icons.
 * @csspart content - Represents the content of the accordion item.
 *
 * @method focusFirstMenuItem - Focus the first menu item in the menu list.
 * @method expandedChanged - Handles expanded changes.
 * @method disabledChanged - Handles disabled changes.
 * @method sizeChanged - Handles changes to size attribute.
 * @method blockChanged - Handles changes to block attribute.
 * @method markerPositionChanged - Handles changes to marker-position attribute.
 *
 * @fires expanded-change - Event fired when the expanded state changes.
 *
 * @extends FASTElement
 * @tagname fabric-accordion-menu-panel
 * @public
 */
export class AccordionMenuPanel extends FASTElement {
    /** Constructor for the AccordionMenuPanel component. */
    constructor() {
        super();
        /** Internal element state used to manage the component. */
        this.elementInternals = this.attachInternals();
        /** The menu list element if present */
        this.menuList = [];
        /**
         * Handles click events.
         * @param e - The click event.
         */
        this.handleClick = (e) => {
            if (this.disabled) {
                e.preventDefault();
                return;
            }
            this.expanded = !this.expanded;
        };
        /**
         * Handles keydown events.
         * @param e - The keyboard event.
         */
        this.handleKeyDown = (e) => {
            var _a;
            if (e.defaultPrevented) {
                return;
            }
            if (e.key === "Tab") {
                return true;
            }
            switch (e.key) {
                case keyEnter:
                case keySpace:
                    e.preventDefault();
                    this.expanded = !this.expanded;
                    break;
                case keyEscape:
                    if (this.expanded) {
                        e.preventDefault();
                        this.expanded = false;
                        (_a = this.headingElement) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                    break;
                default:
                    return;
            }
        };
        /**
         * Expands or collapses the item.
         *
         * @public
         * @remarks
         * HTML attribute: expanded
         */
        this.expanded = false;
        /**
         * Disables an accordion item
         *
         * @public
         * @remarks
         * HTML attribute: disabled
         */
        this.disabled = false;
        /**
         * The item ID
         *
         * @public
         * @remarks
         * HTML Attribute: id
         */
        this.id = uniqueId("accordion-");
        /**
         * Sets the width of the focus state.
         *
         * @public
         * @remarks
         * HTML Attribute: block
         */
        this.block = false;
        this.elementInternals.role = "button";
    }
    /** Lifecycle method called when the component is connected to the DOM. */
    connectedCallback() {
        super.connectedCallback();
    }
    /** Lifecycle method called when the component is disconnected from the DOM. */
    disconnectedCallback() {
        super.disconnectedCallback();
    }
    /**
     * Focus the first menu item in the menu list
     */
    focusFirstMenuItem() {
        if (!this.menuList) {
            return;
        }
        Updates.enqueue(() => {
            var _a;
            if (this.menuList) {
                const menuList = this.menuList;
                if ((_a = menuList[0]) === null || _a === void 0 ? void 0 : _a.menuItems) {
                    const menuItem = menuList[0].menuItems[0];
                    menuItem.focus();
                }
            }
        });
    }
    /**
     * Handles expanded changes
     * @param prev - previous value
     * @param next - next value
     */
    expandedChanged(prev, next) {
        if (next !== prev) {
            toggleState(this.elementInternals, "expanded", next);
            this.$emit("expanded-change", { expanded: next });
            if (next) {
                this.focusFirstMenuItem();
            }
        }
    }
    /**
     * Handles disabled changes
     * @param prev - previous value
     * @param next - next value
     */
    disabledChanged(prev, next) {
        toggleState(this.elementInternals, "disabled", next);
    }
    /**
     * Handles changes to size attribute
     * @param prev - previous value
     * @param next - next value
     */
    sizeChanged(prev, next) {
        if (prev) {
            toggleState(this.elementInternals, prev, false);
        }
        if (next) {
            toggleState(this.elementInternals, next, true);
        }
    }
    /**
     * Handles changes to block attribute
     * @param prev - previous value
     * @param next - next value
     */
    blockChanged(prev, next) {
        toggleState(this.elementInternals, "block", next);
    }
    /**
     * Handles changes to marker-position attribute
     * @param prev - previous value
     * @param next - next value
     */
    markerPositionChanged(prev, next) {
        if (prev) {
            toggleState(this.elementInternals, `align-${prev}`, false);
        }
        if (next) {
            toggleState(this.elementInternals, `align-${next}`, true);
        }
    }
}
__decorate([
    observable
], AccordionMenuPanel.prototype, "menuList", void 0);
__decorate([
    attr({ mode: "boolean" })
], AccordionMenuPanel.prototype, "expanded", void 0);
__decorate([
    attr({ mode: "boolean" })
], AccordionMenuPanel.prototype, "disabled", void 0);
__decorate([
    attr
], AccordionMenuPanel.prototype, "id", void 0);
__decorate([
    attr
], AccordionMenuPanel.prototype, "size", void 0);
__decorate([
    attr({ mode: "boolean" })
], AccordionMenuPanel.prototype, "block", void 0);
__decorate([
    attr({ attribute: "marker-position" })
], AccordionMenuPanel.prototype, "markerPosition", void 0);
//# sourceMappingURL=accordion-menu-panel.js.map